import { Fragment, useState } from 'react';
import { ChevronDown, ChevronRight, CircleCheck } from 'lucide-react';
import type { ProfilerEvent, StageSnapshot } from './profiler.types';
import { SPIKE_THRESHOLD_MS, TICK_BUDGET_MS } from './profiler.health';
import { causeText, fmtBytes, fmtClockSeconds, fmtDay, fmtMs, fmtNumber, fmtShare, stageLabel } from './profiler.format';
import { cx } from './ProfilerParts';
import s from './Profiler.module.scss';

// ============================================================
// Profileur : la liste des pics d'un serveur
// ============================================================
// Un pic est un tick que le moteur a signalé (plus de 200 ms par défaut), avec sa décomposition.
// Un « tick en cours » est un tick qui ne finissait pas au moment du relevé : le monde était figé.
// L'heure est celle du navigateur (heure locale), à la seconde ; la cause est dite en clair, l'indice
// d'origine du moteur (en anglais) reste lisible dans le détail.
// ============================================================

/** Ce qu'un tick a coûté face aux 50 ms dont il disposait : « le temps de 13 ticks ». */
function costText(ms: number): string {
  if (ms < TICK_BUDGET_MS) return `dans les ${TICK_BUDGET_MS} ms disponibles`;
  const ticks = Math.floor(ms / TICK_BUDGET_MS);
  return ticks >= 2 ? `le temps de ${fmtNumber(ticks)} ticks` : 'plus que le temps d\'un tick';
}

function StageLines({ stages, wallMs }: { stages: StageSnapshot[]; wallMs: number }) {
  const shown = stages.filter((st) => st.totalMs >= Math.max(0.05, wallMs * 0.005)).slice(0, 6);
  if (shown.length === 0) return null;
  return (
    <ul className={s.eventStages}>
      {shown.map((st) => (
        <li key={st.name}>
          <span>{stageLabel(st.name)}</span>
          <span className={s.eventStageBar} aria-hidden><span style={{ width: `${Math.min(100, Math.max(1, st.share * 100))}%` }} /></span>
          <strong>{fmtMs(st.totalMs)}</strong>
          <span className={s.muted}>{fmtShare(st.share)}{st.allocatedBytes >= 1024 ? ` · ${fmtBytes(st.allocatedBytes)}` : ''}</span>
        </li>
      ))}
    </ul>
  );
}

function SpikeDetail({ e }: { e: ProfilerEvent }) {
  const cause = causeText(e.cause);
  const wall = e.wallMs ?? 0;
  return (
    <div className={s.eventDetail}>
      <p className={s.eventExplain}><strong>{cause.label}.</strong> {cause.help}</p>
      <dl className={s.facts}>
        <div><dt>Durée du tick</dt><dd>{fmtMs(e.wallMs)} ({costText(wall)})</dd></div>
        <div>
          <dt>Temps de calcul</dt>
          <dd>{e.cpuMs == null ? 'non mesuré sur ce serveur' : `${fmtMs(e.cpuMs)}, soit ${fmtShare(wall > 0 ? Math.min(1, e.cpuMs / wall) : 0)} de la durée`}</dd>
        </div>
        <div><dt>Mémoire prise</dt><dd>{fmtBytes(e.allocatedBytes)}</dd></div>
        <div>
          <dt>Nettoyage de la mémoire</dt>
          <dd>{(e.gcPauseMs ?? 0) > 0 ? `${fmtMs(e.gcPauseMs)} de pause` : 'aucune pause'}</dd>
        </div>
        <div><dt>Retard à la fin du tick</dt><dd>{fmtMs(e.lagMs)}</dd></div>
        <div>
          <dt>Dans le monde</dt>
          <dd>{fmtNumber(e.players ?? 0)} joueur{(e.players ?? 0) > 1 ? 's' : ''}, {fmtNumber(e.entities ?? 0)} entités, {fmtNumber(e.chunks ?? 0)} chunks chargés</dd>
        </div>
      </dl>
      {e.stages && e.stages.length > 0 && (
        <>
          <h4 className={s.eventSubtitle}>Où est parti le temps de ce tick</h4>
          <StageLines stages={e.stages} wallMs={wall} />
          {e.subStages && e.subStages.length > 0 && <StageLines stages={e.subStages} wallMs={wall} />}
        </>
      )}
      {e.hint && <p className={s.eventHint}>Indice du moteur (en anglais) : <code>{e.hint}</code></p>}
      <p className={s.eventHint}>Tick n° {fmtNumber(e.tick ?? 0)}{e.overAllocation && !e.overDuration ? ', signalé pour la mémoire prise et non pour sa durée' : ''}.</p>
    </div>
  );
}

function StallDetail({ e }: { e: ProfilerEvent }) {
  return (
    <div className={s.eventDetail}>
      <p className={s.eventExplain}>
        <strong>Le monde était figé.</strong> Au moment de ce relevé, le tick n° {fmtNumber(e.tick ?? 0)} durait depuis {fmtMs(e.elapsedMs)} et
        n'avait pas fini. Le moteur le signale à 5 s, puis à 10, 20 et 40 s ; le pic correspondant arrive quand le tick se termine.
      </p>
      <dl className={s.facts}>
        <div><dt>Étape en cours</dt><dd>{stageLabel(e.stage)}</dd></div>
        <div>
          <dt>Temps de calcul</dt>
          <dd>{e.cpuMs == null ? 'non mesuré sur ce serveur'
            : `${fmtMs(e.cpuMs)} : ${e.cpuMs >= (e.elapsedMs ?? 0) * 0.9 ? 'le monde calculait sans s\'arrêter' : e.cpuMs <= (e.elapsedMs ?? 0) * 0.5 ? 'le monde attendait' : 'calcul et attente mêlés'}`}</dd>
        </div>
      </dl>
      {e.markers && <p className={s.eventHint}>Ce que le monde traitait (repères du moteur) : <code>{e.markers}</code></p>}
    </div>
  );
}

/**
 * @param now        horloge du satellite, pour n'écrire le jour que lorsqu'il diffère d'aujourd'hui
 * @param multiLevel la liste mêle plusieurs mondes : la colonne « Monde » est affichée
 * @param level      la liste ne porte que sur ce monde d'un serveur qui en a plusieurs ; null sinon
 */
export function EventTable({ events, now, multiLevel, level = null }: { events: ProfilerEvent[]; now: number; multiLevel: boolean; level?: string | null }) {
  const [open, setOpen] = useState<string | null>(null);
  if (events.length === 0) {
    return (
      <p className={cx(s.empty, s.emptyGood)}>
        <CircleCheck size={16} aria-hidden /> Aucun pic gardé pour {level ? `le monde ${level}` : 'ce serveur'} : aucun tick de plus de {SPIKE_THRESHOLD_MS} ms n'a été signalé.
      </p>
    );
  }
  const today = fmtDay(now);
  return (
    <div className={s.tableScrollX}>
      <table className={cx(s.dataTable, s.eventTable)}>
        <thead>
          <tr>
            <th scope="col" className={s.colToggle}><span className={s.srOnly}>Détail</span></th>
            <th scope="col">Heure locale</th>
            {multiLevel && <th scope="col">Monde</th>}
            <th scope="col" className={s.num}>Durée</th>
            <th scope="col">Étape fautive</th>
            <th scope="col">Cause</th>
          </tr>
        </thead>
        <tbody>
          {events.map((e) => {
            const expanded = open === e.id;
            const spike = e.kind === 'spike';
            const day = fmtDay(e.t);
            return (
              <Fragment key={e.id}>
                <tr className={cx(s.eventRow, expanded && s.eventRowOpen)}>
                  <td className={s.colToggle}>
                    <button type="button" className={s.rowToggle} aria-expanded={expanded}
                      aria-label={`${expanded ? 'Replier' : 'Déplier'} le détail du ${spike ? 'pic' : 'tick en cours'} de ${fmtClockSeconds(e.t)}`}
                      onClick={() => setOpen(expanded ? null : e.id)}>
                      {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </button>
                  </td>
                  <th scope="row" className={s.eventTime}>
                    {day !== today && <span className={s.muted}>{day} </span>}{fmtClockSeconds(e.t)}
                  </th>
                  {multiLevel && <td>{e.level ?? '-'}</td>}
                  <td className={s.num}>
                    <strong>{spike ? fmtMs(e.wallMs) : `depuis ${fmtMs(e.elapsedMs)}`}</strong>
                  </td>
                  <td>{stageLabel(e.stage)}</td>
                  <td>{spike ? causeText(e.cause).label : 'Tick encore en cours : le monde était figé'}</td>
                </tr>
                {expanded && (
                  <tr className={s.eventDetailRow}>
                    <td colSpan={multiLevel ? 6 : 5}>{spike ? <SpikeDetail e={e} /> : <StallDetail e={e} />}</td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
